import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { VisibilityRules } from '../BusinessRulesEngine/rules/VisibilityRules';

export interface ConnectionData {
  id: string;
  username: string;
  age: number;
  main_photo_url: string;
  bio: string;
  occupation: string;
  location: string;
  connectionDate: string;
  status: string;
  connectionId?: string;
  interests?: string[];
  mutual?: boolean;
}

export interface ConnectionsResponse {
  sent: ConnectionData[];
  received: ConnectionData[];
  matched: ConnectionData[];
}

class ConnectionsService {
  private static instance: ConnectionsService;

  static getInstance(): ConnectionsService {
    if (!ConnectionsService.instance) {
      ConnectionsService.instance = new ConnectionsService();
    }
    return ConnectionsService.instance;
  }

  async getConnections(userId: string, userProfile: { role: string; hidden: boolean; onboarding_completed: boolean }): Promise<ConnectionsResponse> {
    try {
      debugLogger.debug('ConnectionsService', `Fetching all connections for user: ${userId}`);

      const currentUserProfile = userProfile;

      debugLogger.debug('ConnectionsService', 'User profile loaded:', {
        userId, 
        role: currentUserProfile?.role
      });

      // Query connections with enhanced profile data for Business Rules
      const { data: allConnections, error } = await supabase
        .from('connections')
        .select(`
          id,
          user_id,
          target_id,
          status,
          created_at,
          initiated_by,
          sender_profile:profiles!connections_user_id_fkey (
            id,
            username,
            main_photo_url,
            birth_date,
            bio,
            occupation,
            city,
            state_code,
            interests,
            hidden,
            role,
            onboarding_completed
          ),
          target_profile:profiles!connections_target_id_fkey (
            id,
            username,
            main_photo_url,
            birth_date,
            bio,
            occupation,
            city,
            state_code,
            interests,
            hidden,
            role,
            onboarding_completed
          )
        `)
        .or(`user_id.eq.${userId},target_id.eq.${userId}`)
        .in('status', ['sent', 'matched']);

      if (error) {
        debugLogger.error('ConnectionsService', 'Error fetching connections:', error);
        throw error;
      }

      if (!allConnections) {
        return { sent: [], received: [], matched: [] };
      }

      // Apply Business Rules filtering (async loop)
      const filteredConnections = [];
      for (const conn of allConnections) {
        // Determine which profile to check based on connection direction
        let profileToCheck = null;
        
        if (conn.user_id === userId) {
          profileToCheck = conn.target_profile;
        } else if (conn.target_id === userId) {
          profileToCheck = conn.sender_profile;
        }

        if (!profileToCheck) {
          continue;
        }

        // Apply Business Rules using VisibilityRules
        const visibilityResult = await VisibilityRules.shouldShowInConnections(
          {
            id: userId,
            role: currentUserProfile?.role,
            hidden: currentUserProfile?.hidden || false,
            plan_type: 'free',
            verification_level: 'basic'
          },
          {
            id: profileToCheck.id,
            role: profileToCheck.role,
            hidden: profileToCheck.hidden || false,
            plan_type: 'free',
            verification_level: 'basic',
            onboarding_completed: profileToCheck.onboarding_completed
          }
        );

        if (!visibilityResult.allowed) {
          debugLogger.debug('ConnectionsService', 'Connection filtered by Business Rules:', {
            connectionId: conn.id,
            reason: visibilityResult.reason,
            otherUserId: profileToCheck.id
          });
          continue;
        }

        filteredConnections.push(conn);
      }

      const beforeFilterCount = allConnections.length;
      const afterFilterCount = filteredConnections.length;
      debugLogger.debug('ConnectionsService', 'Business Rules filtering applied:', {
        before: beforeFilterCount, 
        after: afterFilterCount, 
        filtered: beforeFilterCount - afterFilterCount 
      });

      // Categorize filtered connections
      const sent: ConnectionData[] = [];
      const received: ConnectionData[] = [];
      const matched: ConnectionData[] = [];

      filteredConnections.forEach(conn => {
        if (conn.status === 'matched') {
          const otherProfile = conn.user_id === userId 
            ? conn.target_profile 
            : conn.sender_profile;

          if (otherProfile) {
            matched.push(this.transformConnection({
              ...conn,
              profiles: otherProfile
            }));
          }
        } else if (conn.status === 'sent') {
          if (conn.user_id === userId && conn.initiated_by === userId) {
            if (conn.target_profile) {
              sent.push(this.transformConnection({
                ...conn,
                profiles: conn.target_profile
              }));
            }
          } else if (conn.target_id === userId && conn.initiated_by !== userId) {
            if (conn.sender_profile) {
              received.push(this.transformConnection({
                ...conn,
                profiles: conn.sender_profile
              }));
            }
          }
        }
      });

      debugLogger.debug('ConnectionsService', 'Connections categorized:', {
        sent: sent.length,
        received: received.length,
        matched: matched.length,
        total: filteredConnections.length
      });

      return { sent, received, matched };
    } catch (error) {
      debugLogger.error('ConnectionsService', 'Error processing connections:', error);
      throw new Error('Erro ao carregar conexões');
    }
  }

  private transformConnection(conn: any): ConnectionData {
    const profile = conn.profiles;
    if (!profile) {
      throw new Error('Profile data missing for connection');
    }

    debugLogger.debug('ConnectionsService', 'Transforming connection:', {
      connectionId: conn.id,
      profileId: profile.id,
      username: profile.username,
      hidden: profile.hidden,
      role: profile.role,
      onboarding_completed: profile.onboarding_completed
    });

    return {
      id: profile.id,
      username: profile.username || 'Usuário',
      age: this.calculateAge(profile.birth_date),
      main_photo_url: profile.main_photo_url || '',
      bio: profile.bio || '',
      occupation: profile.occupation || '',
      location: this.formatLocation(profile.city, profile.state_code),
      connectionDate: conn.created_at,
      status: conn.status,
      connectionId: conn.id,
      interests: Array.isArray(profile.interests) ? profile.interests : [],
      mutual: false
    };
  }

  private calculateAge(birthDate: string | null): number {
    if (!birthDate) return 0;
    
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return age;
    } catch {
      return 0;
    }
  }

  private formatLocation(city: string | null, stateCode: string | null): string {
    if (city && stateCode) {
      return `${city}, ${stateCode}`;
    }
    return city || stateCode || 'Localização não informada';
  }
}

export const connectionsService = ConnectionsService.getInstance();
